import api from '../utils/api';

/**
 * Authentication Service - Handles all auth-related API calls
 */

export const authService = {
  /**
   * Register a new user
   * @param {Object} userData - { name, email, password, role }
   * @returns {Promise}
   */
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      // Backend returns { success: true, data: { user, token } }
      const result = response.data.data || response.data;
      const { token, user } = result;
      
      // Store token and user data
      if (token) {
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userRole', user.role);
      }
      
      return result;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.join(', ') || 
                          error.message || 
                          'Registration failed';
      throw new Error(errorMessage);
    }
  },

  /**
   * Login with email and password
   * @param {string} email
   * @param {string} password
   * @returns {Promise}
   */
  login: async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      // Backend returns { success: true, data: { user, token } }
      const result = response.data.data || response.data;
      const { token, user } = result;
      
      // Store token and user data
      if (token) {
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userRole', user.role);
      }
      
      return result;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.join(', ') || 
                          error.message || 
                          'Login failed';
      throw new Error(errorMessage);
    }
  },

  /**
   * Phone OTP login - verify OTP then get/create user and return token
   * @param {string} mobileNumber - 10-digit mobile
   * @param {string} otp - 6-digit OTP
   * @returns {Promise}
   */
  phoneLogin: async (mobileNumber, otp) => {
    try {
      const portal = localStorage.getItem('authPortal') || 'new';
      const response = await api.post('/auth/phone-login', { mobileNumber, otp, portal });
      const result = response.data?.data || response.data;
      const { token, user, isNewUser } = result;
      if (token) {
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userRole', user?.role || 'pending');
      }
      return { ...result, user, token, isNewUser };
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Phone login failed';
      throw new Error(msg);
    }
  },

  /**
   * Google OAuth login
   * @param {string} googleToken - Google OAuth token from frontend
   * @returns {Promise}
   */
  googleLogin: async (googleToken) => {
    try {
      const response = await api.post('/auth/google-login', { token: googleToken });
      const result = response.data?.data || response.data;
      const { token, user } = result || {};
      if (token) {
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userRole', user?.role || 'pending');
      }
      return { token, user };
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Google login failed');
    }
  },

  /**
   * Facebook OAuth login
   * @param {string} accessToken - Facebook access token from FB.login
   */
  facebookLogin: async (accessToken) => {
    try {
      const response = await api.post('/auth/facebook-login', { token: accessToken });
      const result = response.data?.data || response.data;
      const { token, user } = result || {};
      if (token) {
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userRole', user?.role || 'pending');
      }
      return { token, user };
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Facebook login failed');
    }
  },

  /**
   * Get current user profile
   * @returns {Promise}
   */
  getProfile: async () => {
    try {
      const response = await api.get('/auth/profile');
      const payload = response.data?.data || response.data;
      const user = payload?.user || payload;
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
        if (user.role) {
          localStorage.setItem('userRole', user.role);
        }
      }
      return { data: { user }, user };
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch profile');
    }
  },

  /**
   * Step 1 of password reset: send OTP to the user's registered phone.
   * @param {string} identifier - email or phone number
   * @returns {Promise<{ resetToken: string, maskedPhone: string, devOtp?: string }>}
   */
  forgotPassword: async (identifier) => {
    try {
      const response = await api.post('/auth/forgot-password', { identifier });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to send OTP. Please try again.');
    }
  },

  /**
   * Step 2 of password reset: verify OTP and set new password.
   * @param {string} resetToken - token from forgotPassword response
   * @param {string} otp - 6-digit OTP entered by user
   * @param {string} newPassword
   */
  resetPassword: async (resetToken, otp, newPassword) => {
    try {
      const response = await api.post('/auth/reset-password', { resetToken, otp, newPassword });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Password reset failed. Please try again.');
    }
  },

  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.put('/auth/change-password', {
        currentPassword: currentPassword || '',
        newPassword
      });
      const user = response.data?.data?.user || response.data?.user;
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      }
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to change password');
    }
  },

  /**
   * Complete onboarding - set role and role-specific details (first-time login)
   * @param {Object} data - { role, vendorDetails?, mechanicDetails?, garageDetails? }
   */
  completeOnboarding: async (data) => {
    try {
      const response = await api.put('/auth/complete-onboarding', data);
      const user = response.data?.data?.user || response.data?.user || response.data;
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('userRole', user.role);
      }
      return user;
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Failed to complete onboarding';
      throw new Error(msg);
    }
  },

  /**
   * Upload file (base64) - returns full URL to stored file
   * @param {string} content - base64 string
   * @param {string} fileName
   * @param {string} mimeType
   * @returns {Promise<string>} full file URL
   */
  uploadFile: async (content, fileName, mimeType) => {
    const response = await api.post('/registration/upload', { content, fileName, mimeType });
    const data = response.data?.data || response.data;
    const path = data?.path || '';
    const base = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');
    return path.startsWith('http') ? path : `${base}${path}`;
  },

  /**
   * Upload a File/Blob via multipart — images are compressed before upload.
   * @param {File|Blob} file
   * @param {Object} [options] - passed to image compressor (e.g. { cameraOnly: true })
   * @returns {Promise<string>} full file URL
   */
  uploadFileBlob: async (file, options = {}) => {
    let uploadFile = file;
    const skipCompress = Boolean(options.skipCompress);
    const isImage = file?.type?.startsWith('image/')
      || /\.(jpe?g|png|webp|heic|heif|gif|bmp)$/i.test(file?.name || '');
    if (!skipCompress && isImage) {
      const { prepareStoredUploadFile } = await import('../component/auth/partnerUploadFile');
      const prepared = await prepareStoredUploadFile(file, options);
      uploadFile = prepared.file || prepared;
    }
    const formData = new FormData();
    const uploadName = uploadFile.name || file.name || 'upload.jpg';
    formData.append('file', uploadFile, uploadName);
    // Do not set Content-Type manually — the browser must add the multipart boundary.
    // Forcing "multipart/form-data" without a boundary makes multer miss req.file.
    const response = await api.post('/registration/upload', formData, {
      headers: { 'Content-Type': false },
      timeout: 180000,
    });
    const data = response.data?.data || response.data;
    const path = data?.path || '';
    const base = (api.defaults.baseURL || '').replace(/\/api\/?$/, '');
    return path.startsWith('http') ? path : `${base}${path}`;
  },

  /**
   * Compress image then upload (PDFs upload as-is). Use for garage / seller file pickers.
   * @param {File|Blob} file
   * @param {Object} [options]
   * @returns {Promise<string>} full file URL
   */
  uploadCompressedFile: async (file, options = {}) => {
    const { compressAndUploadFile } = await import('../component/auth/partnerUploadFile');
    return compressAndUploadFile(file, options);
  },

  /**
   * Logout - clear local storage
   */
  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    // Vehicle selection is persisted across sessions; clear it on logout
    // so users don't see an old car "selected" after sign-out.
    localStorage.removeItem('selectedVehicle');
    localStorage.removeItem('vehicles');
  },

  /**
   * Check if user is authenticated
   * @returns {boolean}
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('authToken');
  },

  /**
   * Get stored JWT token
   * @returns {string|null}
   */
  getToken: () => {
    return localStorage.getItem('authToken');
  },

  /**
   * Get stored user data
   * @returns {Object|null}
   */
  getUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
};

/**
 * OTP Service - Handles OTP-related API calls
 */
export const otpService = {
  /**
   * Send OTP to mobile number
   * @param {string} mobileNumber - 10-digit mobile number
   * @returns {Promise}
   */
  sendOTP: async (mobileNumber) => {
    try {
      console.log('Sending OTP request to:', '/auth/otp/send', 'with mobile:', mobileNumber);
      const portal = localStorage.getItem('authPortal') || 'new';
      const response = await api.post('/auth/otp/send', { mobileNumber, portal });
      console.log('OTP response:', response.data);
      return response.data;
    } catch (error) {
      console.error('OTP send error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to send OTP';
      throw new Error(errorMessage);
    }
  },

  /**
   * Verify OTP
   * @param {string} mobileNumber - 10-digit mobile number
   * @param {string} otp - 6-digit OTP
   * @returns {Promise}
   */
  verifyOTP: async (mobileNumber, otp) => {
    try {
      const portal = localStorage.getItem('authPortal') || 'new';
      const response = await api.post('/auth/otp/verify', { mobileNumber, otp, portal });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'OTP verification failed';
      throw new Error(errorMessage);
    }
  },

  /**
   * Resend OTP
   * @param {string} mobileNumber - 10-digit mobile number
   * @returns {Promise}
   */
  resendOTP: async (mobileNumber) => {
    try {
      const portal = localStorage.getItem('authPortal') || 'new';
      const response = await api.post('/auth/otp/resend', { mobileNumber, portal });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to resend OTP';
      throw new Error(errorMessage);
    }
  },
};

/**
 * User Service - Handles all user-related API calls
 */

export const userService = {
  /**
   * Get all users
   * @returns {Promise}
   */
  getAllUsers: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      // Support structured filters: location (legacy), area, city, state, country, q
      if (filters.location) params.append('location', filters.location);
      if (filters.area) params.append('area', filters.area);
      if (filters.city) params.append('city', filters.city);
      if (filters.state) params.append('state', filters.state);
      if (filters.country) params.append('country', filters.country);
      if (filters.q) params.append('q', filters.q);
      if (filters.role) params.append('role', filters.role);
      const queryString = params.toString();
      const url = queryString ? `/users?${queryString}` : '/users';
      const response = await api.get(url);
      const data = response.data?.data ?? response.data;
      return Array.isArray(data) ? data : (data?.users ?? data ?? []);
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch users');
    }
  },

  /**
   * Get user by ID
   * @param {string} userId
   * @returns {Promise}
   */
  getUserById: async (userId) => {
    try {
      const response = await api.get(`/users/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch user');
    }
  },

  /**
   * Get order count for a user (admin)
   * @param {string} userId
   * @returns {Promise<{ count: number }>}
   */
  getOrderCount: async (userId) => {
    try {
      const response = await api.get(`/users/${userId}/order-count`);
      const data = response.data?.data ?? response.data;
      return { count: data?.count ?? 0 };
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch order count');
    }
  },

  /**
   * Update user by ID
   * @param {string} userId
   * @param {Object} userData
   * @returns {Promise}
   */
  updateUser: async (userId, userData) => {
    try {
      const response = await api.put(`/users/${userId}`, userData);
      // Backend returns { success: true, data: { user } }
      const result = response.data.data || response.data;
      
      // If updating current user, update localStorage
      const currentUser = authService.getUser();
      if (currentUser && (currentUser.id === userId || currentUser._id === userId)) {
        const updatedUser = result.user || result;
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      
      return result;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.join(', ') || 
                          error.message || 
                          'Failed to update user';
      throw new Error(errorMessage);
    }
  },

  /**
   * Delete user by ID
   * @param {string} userId
   * @returns {Promise}
   */
  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/users/${userId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete user');
    }
  },
};

/**
 * Cart Service - Handles all cart-related API calls
 */
export const cartService = {
  /**
   * Get user's cart
   * @returns {Promise}
   */
  getCart: async () => {
    try {
      const response = await api.get('/cart');
      // Backend returns { success: true, data: { cart } }
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch cart');
    }
  },

  /**
   * Add item to cart
   * @param {Object} item - Cart item data
   * @returns {Promise}
   */
  addItem: async (item) => {
    try {
      const response = await api.post('/cart/items', item);
      // Backend returns { success: true, data: { cart } }
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add item to cart');
    }
  },

  /**
   * Update cart item quantity
   * @param {string} productId
   * @param {number} quantity
   * @param {string} partNumber - Optional part number
   * @returns {Promise}
   */
  updateItem: async (productId, quantity, partNumber = null, dealId = null) => {
    try {
      const response = await api.put('/cart/items', { productId, quantity, partNumber, dealId });
      // Backend returns { success: true, data: { cart } }
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update cart item');
    }
  },

  /**
   * Remove item from cart
   * @param {string} productId
   * @param {string} partNumber - Optional part number
   * @returns {Promise}
   */
  removeItem: async (productId, partNumber = null, dealId = null) => {
    try {
      const response = await api.delete('/cart/items', { 
        data: { productId, partNumber, dealId } 
      });
      // Backend returns { success: true, data: { cart } }
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to remove item from cart');
    }
  },

  /**
   * Clear cart
   * @returns {Promise}
   */
  clearCart: async () => {
    try {
      const response = await api.delete('/cart');
      // Backend returns { success: true, data: { cart } }
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to clear cart');
    }
  },

  applyCoupon: async (code, paymentMethod = null) => {
    try {
      const response = await api.post('/cart/coupon/apply', { code, paymentMethod });
      return response.data.data || response.data;
    } catch (error) {
      const err = new Error(error.response?.data?.message || 'Failed to apply coupon');
      err.failures = error.response?.data?.failures || error.response?.data?.errors || [];
      err.response = error.response;
      throw err;
    }
  },

  removeCoupon: async () => {
    try {
      const response = await api.delete('/cart/coupon');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to remove coupon');
    }
  },
};

export const checkoutService = {
  getEnrichedCart: async () => {
    const response = await api.get('/cart');
    return response.data?.data || response.data;
  },

  getQuote: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.addressId) qs.set('addressId', params.addressId);
    if (params.pincode) qs.set('pincode', params.pincode);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await api.get(`/orders/checkout-quote${suffix}`);
    return response.data?.data || response.data;
  },

  getAddresses: async () => {
    const response = await api.get('/user/addresses');
    return response.data?.data?.addresses || response.data?.addresses || [];
  },

  addAddress: async (payload) => {
    const response = await api.post('/user/addresses', payload);
    return response.data?.data?.address || response.data?.address;
  },

  getCodPincodes: async () => {
    const response = await api.get('/config/cod-pincodes');
    return response.data?.data || response.data;
  },

  createCheckoutOrder: async (payload) => {
    try {
      const response = await api.post('/orders/create', payload);
      return response.data?.data || response.data;
    } catch (error) {
      const err = new Error(error.response?.data?.message || 'Checkout failed');
      err.status = error.response?.status;
      err.code = error.response?.data?.code;
      err.items = error.response?.data?.items;
      throw err;
    }
  },

  removeCartItem: async (itemId) => {
    const response = await api.delete(`/cart/items/${itemId}`);
    return response.data?.data || response.data;
  },

  getPendingCheckout: async () => {
    const response = await api.get('/orders/pending-checkout');
    return response.data?.data ?? null;
  },

  restorePendingCart: async () => {
    const response = await api.post('/orders/restore-pending-cart');
    return response.data?.data || response.data;
  },

  getMarketplaceQuote: async ({ catalogNodeId, listingId, addressId }) => {
    const qs = new URLSearchParams({ catalogNodeId, listingId });
    if (addressId) qs.set('addressId', addressId);
    try {
      const response = await api.get(`/orders/checkout-quote?${qs.toString()}`);
      return response.data?.data || response.data;
    } catch (error) {
      const err = new Error(error.response?.data?.message || 'Could not load checkout quote');
      err.status = error.response?.status;
      err.code = error.response?.data?.code;
      throw err;
    }
  },

  createMarketplaceOrder: async (payload) => {
    try {
      const response = await api.post('/orders/marketplace-checkout', payload);
      return response.data?.data || response.data;
    } catch (error) {
      const err = new Error(error.response?.data?.message || 'Checkout failed');
      err.status = error.response?.status;
      err.code = error.response?.data?.code;
      throw err;
    }
  },
};

/** B-07P — Storefront Razorpay payment (verify / status) */
export const storefrontPaymentService = {
  verifyPayment: async (payload) => {
    try {
      const response = await api.post('/payment/verify', payload);
      return response.data?.data || response.data;
    } catch (error) {
      const err = new Error(error.response?.data?.message || 'Payment verification failed');
      err.status = error.response?.status;
      err.code = error.response?.data?.code;
      err.retryCount = error.response?.data?.retryCount;
      throw err;
    }
  },

  getPaymentStatus: async (orderId) => {
    const response = await api.get(`/payment/status/${orderId}`);
    return response.data?.data || response.data;
  },

  recordFailure: async (orderId, reason) => {
    const response = await api.post('/payment/failure', { orderId, reason });
    return response.data?.data || response.data;
  },

  getUpiCollect: async (orderId) => {
    const response = await api.get(`/payment/upi-collect/${orderId}`);
    return response.data?.data || response.data;
  },

  createDemoSignature: async (payload) => {
    const response = await api.post('/payment/demo-signature', payload);
    return response.data?.data || response.data;
  },
};

/** B-08 — Order tracking (12 states) */
export const orderTrackingService = {
  getOrder: async (orderId, mockState) => {
    const qs = mockState ? `?mockState=${encodeURIComponent(mockState)}` : '';
    const response = await api.get(`/orders/${orderId}${qs}`);
    return response.data?.data || response.data;
  },

  getTrackingEvents: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/tracking`);
    return response.data?.data || response.data;
  },

  confirmDelivery: async (orderId, otp) => {
    const response = await api.post(`/orders/${orderId}/confirm-delivery`, { otp });
    return response.data?.data || response.data;
  },

  verifyDeliveryOtp: async (orderId, otp) => {
    const response = await api.post(`/orders/${orderId}/verify-delivery-otp`, { otp });
    return response.data?.data || response.data;
  },

  cancelOrder: async (orderId, reason, { payCancelFee = false } = {}) => {
    try {
      const response = await api.post(`/orders/${orderId}/cancel`, { reason, payCancelFee });
      return response.data?.data || response.data;
    } catch (error) {
      const err = new Error(error.response?.data?.message || 'Cancel failed');
      err.code = error.response?.data?.code;
      err.cancelFee = error.response?.data?.cancelFee;
      throw err;
    }
  },

  cancelInTransit: async (orderId, reason) => {
    try {
      const response = await api.post(`/orders/${orderId}/cancel-in-transit`, { reason });
      return response.data?.data || response.data;
    } catch (error) {
      const err = new Error(error.response?.data?.message || 'Cancel failed');
      err.code = error.response?.data?.code;
      throw err;
    }
  },

  getReassignmentOffer: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/reassignment`);
    return response.data?.data || response.data;
  },

  acceptReassignment: async (orderId, { approvePriceDiff = false } = {}) => {
    const response = await api.post(`/orders/${orderId}/reassignment/accept`, {
      approvePriceDiff,
    });
    return response.data?.data || response.data;
  },

  rejectReassignment: async (orderId, reason) => {
    const response = await api.post(`/orders/${orderId}/reassignment/reject`, { reason });
    return response.data?.data || response.data;
  },

  reportPackageContact: async (orderId, notes = '') => {
    try {
      const response = await api.post(`/orders/${orderId}/report-package-contact`, { notes });
      return response.data?.data || response.data;
    } catch (error) {
      const err = new Error(error.response?.data?.message || 'Report failed');
      err.code = error.response?.data?.code;
      throw err;
    }
  },

  /** B-08D — delivery handoff OTP (OUT_FOR_DELIVERY only) */
  getDeliveryOtp: async (orderId, mockState) => {
    try {
      const qs = mockState ? `?mockState=${encodeURIComponent(mockState)}` : '';
      const response = await api.get(`/orders/${orderId}/delivery-otp${qs}`);
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load delivery OTP');
    }
  },
};

/** B-09 — Returns & disputes */
export const disputeService = {
  getClaimEligibleOrders: async () => {
    try {
      const response = await api.get('/orders?state=DELIVERED&claimEligible=true');
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load eligible orders');
    }
  },

  listDisputes: async (bucket) => {
    try {
      const qs = bucket ? `?bucket=${encodeURIComponent(bucket)}` : '';
      const response = await api.get(`/disputes${qs}`);
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load disputes');
    }
  },

  createDispute: async (payload) => {
    try {
      const response = await api.post('/disputes/create', payload);
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to submit claim');
    }
  },

  getDispute: async (orderId) => {
    try {
      const response = await api.get(`/disputes/${encodeURIComponent(orderId)}`);
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load dispute');
    }
  },

  uploadPhotos: async (orderId, photos) => {
    try {
      const response = await api.post(`/disputes/${orderId}/photos`, { photos });
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload photos');
    }
  },

  submitEvidenceResponse: async (orderId, payload) => {
    try {
      const response = await api.post(`/disputes/${orderId}/evidence-response`, payload);
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to submit evidence');
    }
  },

  submitAppeal: async (orderId, payload) => {
    try {
      const response = await api.post(`/disputes/${orderId}/appeal`, payload);
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to submit appeal');
    }
  },

  getAppeal: async (orderId) => {
    try {
      const response = await api.get(`/disputes/${orderId}/appeal`);
      return response.data?.data || response.data;
    } catch (error) {
      if (error.response?.status === 404) return null;
      throw new Error(error.response?.data?.message || 'Failed to load appeal');
    }
  },
};

/** B-09 seller — dispute evidence & order claims */
export const disputeSellerService = {
  listDisputes: async (bucket = 'active') => {
    try {
      const qs = bucket ? `?bucket=${encodeURIComponent(bucket)}` : '';
      const response = await api.get(`/disputes/seller/list${qs}`);
      return response.data?.data?.disputes || response.data?.disputes || [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load seller disputes');
    }
  },

  getDispute: async (orderId) => {
    try {
      const response = await api.get(`/disputes/seller/${encodeURIComponent(orderId)}`);
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load dispute');
    }
  },

  submitEvidenceResponse: async (orderId, payload) => {
    try {
      const response = await api.post(
        `/disputes/seller/${encodeURIComponent(orderId)}/evidence-response`,
        { ...payload, party: 'seller' }
      );
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to submit seller evidence');
    }
  },
};

/** B-09 admin — dispute arbitration */
export const disputeAdminService = {
  listDisputes: async (bucket = 'active') => {
    try {
      const qs = bucket ? `?status=${encodeURIComponent(bucket)}` : '';
      const response = await api.get(`/disputes/admin/list${qs}`);
      return response.data?.data?.disputes || response.data?.disputes || [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load disputes');
    }
  },

  getDispute: async (claimId) => {
    try {
      const response = await api.get(`/disputes/admin/${encodeURIComponent(claimId)}`);
      return response.data?.data?.dispute || response.data?.dispute;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load dispute');
    }
  },

  requestEvidence: async (claimId, { party, description }) => {
    try {
      const response = await api.post(
        `/disputes/admin/${encodeURIComponent(claimId)}/evidence-request`,
        { party, description }
      );
      return response.data?.data?.dispute || response.data?.dispute;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to request evidence');
    }
  },

  resolve: async (claimId, payload) => {
    try {
      const response = await api.post(
        `/disputes/admin/${encodeURIComponent(claimId)}/resolve`,
        payload
      );
      return response.data?.data?.dispute || response.data?.dispute;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to resolve dispute');
    }
  },

  listAppeals: async (status = 'open') => {
    try {
      const response = await api.get(`/disputes/admin/appeals?status=${encodeURIComponent(status)}`);
      return response.data?.data?.appeals || response.data?.appeals || [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load appeals');
    }
  },

  resolveAppeal: async (appealId, payload) => {
    try {
      const response = await api.post(
        `/disputes/admin/appeals/${encodeURIComponent(appealId)}/resolve`,
        payload
      );
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to resolve appeal');
    }
  },
};

/**
 * Order Service - Handles all order-related API calls
 */
export const orderService = {
  /**
   * Create a new order
   * @param {Object} orderData - Order data
   * @returns {Promise}
   */
  createOrder: async (orderData) => {
    try {
      const response = await api.post('/orders', orderData);
      // Backend returns { success: true, data: { order } }
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create order');
    }
  },

  /**
   * Get all orders for current user
   * @param {Object} filters - Optional filters (e.g., { status: 'Delivered' })
   * @returns {Promise}
   */
  getOrders: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) {
        params.append('status', filters.status);
      }
      const queryString = params.toString();
      const url = queryString ? `/orders?${queryString}` : '/orders';
      const response = await api.get(url);
      // Backend returns { success: true, data: { orders } }
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch orders');
    }
  },

  /**
   * VENDOR: Get vendor orders
   * @param {Object} filters - Optional filters (e.g., { status: 'Delivered' })
   * @returns {Promise}
   */
  getVendorOrders: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) {
        params.append('status', filters.status);
      }
      const queryString = params.toString();
      const url = queryString ? `/vendor/orders?${queryString}` : '/vendor/orders';
      const response = await api.get(url);
      // Backend returns { success: true, data: { orders, summary } }
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch vendor orders');
    }
  },

  /**
   * Get order by ID
   * @param {string} orderId
   * @returns {Promise}
   */
  getOrderById: async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch order');
    }
  },

  /**
   * Confirm advance/full payment and permanently lock garage service slot.
   */
  confirmOrderPayment: async (orderId) => {
    try {
      const response = await api.post(`/orders/${orderId}/confirm-payment`);
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to confirm payment');
    }
  },

  /**
   * Garage service deal capacity for a date / optional time slot.
   */
  getGarageBookingStatus: async (dealId, { date, timeSlot } = {}) => {
    try {
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      if (timeSlot) params.set('timeSlot', timeSlot);
      const qs = params.toString();
      const url = `/deals/${dealId}/garage-booking-status${qs ? `?${qs}` : ''}`;
      const response = await api.get(url);
      return response.data?.data?.booking || response.data?.booking || null;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to check garage booking availability');
    }
  },

  /**
   * Initiate return for an order
   * @param {Object} returnData - Return data { orderId, items, reason, pickupDate, pickupTimeSlot }
   * @returns {Promise}
   */
  initiateReturn: async (returnData) => {
    try {
      const response = await api.post('/orders/return', returnData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to initiate return');
    }
  },

  /**
   * Get return status for an order
   * @param {string} orderId
   * @returns {Promise}
   */
  getReturnStatus: async (orderId) => {
    try {
      const response = await api.get(`/orders/${orderId}/return`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch return status');
    }
  },

  /**
   * Subscribe to push notifications
   * @param {Object} subscription - Push subscription object
   * @returns {Promise}
   */
  subscribeToNotifications: async (subscription) => {
    try {
      const response = await api.post('/notifications/subscribe', { subscription });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to subscribe to notifications');
    }
  },

  /**
   * Unsubscribe from push notifications
   * @returns {Promise}
   */
  unsubscribeFromNotifications: async () => {
    try {
      const response = await api.post('/notifications/unsubscribe');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to unsubscribe from notifications');
    }
  },

  /**
   * Update order status
   * @param {string} orderId
   * @param {string} status
   * @param {string} cancellationReason - Optional cancellation reason
   * @returns {Promise}
   */
  updateOrderStatus: async (orderId, status, cancellationReason = null) => {
    try {
      const response = await api.put(`/orders/${orderId}/status`, {
        status,
        cancellationReason
      });
      // Backend returns { success: true, data: { order } }
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update order');
    }
  },

  /**
   * Cancel selected items in an order (partial cancellation). Reason is visible to vendor and superadmin.
   * @param {string} orderId
   * @param {string[]} itemProductIds - productId of items to cancel
   * @param {string} cancellationReason
   * @returns {Promise}
   */
  cancelOrderItems: async (orderId, itemProductIds, cancellationReason) => {
    try {
      const response = await api.post(`/orders/${orderId}/cancel-items`, {
        itemProductIds,
        cancellationReason
      });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to cancel items');
    }
  },

  /**
   * Delete order
   * @param {string} orderId
   * @returns {Promise}
   */
  deleteOrder: async (orderId) => {
    try {
      const response = await api.delete(`/orders/${orderId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete order');
    }
  },

  /**
   * ADMIN: Get all orders
   * @param {Object} filters - Filters (status, paymentStatus, startDate, endDate)
   * @param {number} skip - Pagination skip
   * @param {number} limit - Pagination limit
   * @returns {Promise}
   */
  getAllOrders: async (filters = {}, skip = 0, limit = 50) => {
    try {
      const params = new URLSearchParams();
      params.append('skip', skip);
      params.append('limit', limit);
      
      if (filters.status) {
        params.append('status', filters.status);
      }
      if (filters.paymentStatus) {
        params.append('paymentStatus', filters.paymentStatus);
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }
      
      const response = await api.get(`/orders/admin/all?${params.toString()}`);
      // Backend returns { success: true, data: { orders, total, pages } }
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch all orders');
    }
  },

  /**
   * ADMIN: Get order statistics
   * @returns {Promise}
   */
  getOrderStats: async () => {
    try {
      const response = await api.get('/orders/admin/stats');
      // Backend returns { success: true, data: { totalOrders, paidOrders, deliveredOrders, etc } }
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch order stats');
    }
  },

  reassignMarketplaceOrder: async (orderId, { listingId } = {}) => {
    try {
      const response = await api.post(`/orders/admin/${orderId}/reassign`, { listingId });
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Reassignment failed');
    }
  },
};

/**
 * Product Service - Handles all product-related API calls
 */
export const productService = {
  /**
   * SUPERADMIN: Create product (auto-approved). Same payload as vendor create.
   * POST /api/admin/products
   */
  createAdminProduct: async (productData) => {
    try {
      const response = await api.post('/admin/products', productData);
      return response.data?.data?.product ?? response.data?.data ?? response.data;
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        (Array.isArray(error.response?.data?.errors)
          ? error.response.data.errors.join('; ')
          : null) ||
        error.message;
      throw new Error(msg || 'Failed to create product');
    }
  },

  /**
   * Get all products (admin only)
   * @param {Object} filters - Optional filters (e.g., { status: 'pending' } or { approved: false })
   * @returns {Promise}
   */
  getAdminProducts: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      // Support both status and approved for backward compatibility
      if (filters.status) {
        params.append('status', filters.status);
      } else if (filters.approved !== undefined) {
        // Convert boolean to string for query parameter
        params.append('approved', filters.approved === false ? 'false' : String(filters.approved));
      }
      if (filters.vendorId) {
        params.append('vendorId', filters.vendorId);
      }
      if (filters.category) {
        params.append('category', filters.category);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.page) {
        params.append('page', filters.page);
      }
      if (filters.limit) {
        params.append('limit', filters.limit);
      }
      if (filters.isActive !== undefined) {
        params.append('isActive', filters.isActive);
      }
      
      const queryString = params.toString();
      const url = queryString ? `/admin/products?${queryString}` : '/admin/products';
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch products');
    }
  },

  /**
   * Approve product (admin only)
   * @param {string} productId
   * @returns {Promise}
   */
  approveProduct: async (productId) => {
    try {
      const response = await api.put(`/admin/products/${productId}/approve`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to approve product');
    }
  },

  /**
   * Reject product (admin only)
   * @param {string} productId
   * @returns {Promise}
   */
  rejectProduct: async (productId) => {
    try {
      const response = await api.put(`/admin/products/${productId}/reject`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reject product');
    }
  },

  /**
   * ADMIN: Update product
   * @param {string} productId
   * @param {Object} productData
   * @returns {Promise}
   */
  updateAdminProduct: async (productId, productData) => {
    try {
      const response = await api.put(`/admin/products/${productId}`, productData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update product');
    }
  },

  /**
   * ADMIN: Delete/Reject product
   * @param {string} productId
   * @returns {Promise}
   */
  deleteAdminProduct: async (productId) => {
    try {
      const response = await api.delete(`/admin/products/${productId}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete product');
    }
  },

  /**
   * Get user products (approved products only)
   * @param {Object} filters - Optional filters
   * @returns {Promise}
   */
  getUserProducts: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.subCategory) params.append('subCategory', filters.subCategory);
      if (filters.search) params.append('search', filters.search);
      if (filters.brand) params.append('brand', filters.brand);
      if (filters.minPrice) params.append('minPrice', filters.minPrice);
      if (filters.maxPrice) params.append('maxPrice', filters.maxPrice);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      // Vehicle compatibility filters
      if (filters.vehicleMake) params.append('vehicleMake', filters.vehicleMake);
      if (filters.vehicleModel) params.append('vehicleModel', filters.vehicleModel);
      if (filters.vehicleYear) params.append('vehicleYear', filters.vehicleYear);
      if (filters.vehicleVariant) params.append('vehicleVariant', filters.vehicleVariant);
      if (filters.vehicleFuelType) params.append('vehicleFuelType', filters.vehicleFuelType);
      // Spare condition filter: 'new' | 'used'
      if (filters.condition) params.append('condition', filters.condition);
      // Canonical product type filter: NEW | AFTERMARKET | USED
      if (filters.partType) params.append('partType', filters.partType);
      if (filters.latitude != null) params.append('latitude', filters.latitude);
      if (filters.longitude != null) params.append('longitude', filters.longitude);
      if (filters.lat != null && filters.latitude == null) params.append('latitude', filters.lat);
      if (filters.lng != null && filters.longitude == null) params.append('longitude', filters.lng);
      if (filters.radiusKm != null) params.append('radiusKm', filters.radiusKm);
      if (filters.subSystem) params.append('subSystem', filters.subSystem);
      if (filters.listingCondition) params.append('listingCondition', filters.listingCondition);
      if (filters.sellerTypes) params.append('sellerTypes', filters.sellerTypes);
      if (filters.minRating != null) params.append('minRating', filters.minRating);
      if (filters.proximity) params.append('proximity', filters.proximity);
      if (filters.expressDelivery) params.append('expressDelivery', 'true');

      const queryString = params.toString();
      const url = queryString ? `/products?${queryString}` : '/products';
      const response = await api.get(url);
      // Backend returns { success, data: { products } }
      const data = response.data?.data || response.data;
      const products = data?.products ?? data?.items ?? data ?? [];
      return { ...data, products };
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch products');
    }
  },

  /**
   * Get product by ID
   * @param {string} productId
   * @returns {Promise}
   */
  getProductById: async (productId) => {
    try {
      const response = await api.get(`/products/${productId}`);
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch product');
    }
  },
  /**
   * ADMIN: Get product by ID with reviews and rating (admin view)
   * @param {string} productId
   * @returns {Promise}
   */
  getAdminProductById: async (productId, params = {}) => {
    try {
      const query = new URLSearchParams(params).toString();
      const url = query ? `/admin/products/${productId}?${query}` : `/admin/products/${productId}`;
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch admin product details');
    }
  },

  /**
   * Get all categories
   * @returns {Promise}
   */
  getCategories: async (vehicleParams = {}) => {
    try {
      const params = new URLSearchParams();
      if (vehicleParams.vehicleMake) params.append('vehicleMake', vehicleParams.vehicleMake);
      if (vehicleParams.vehicleModel) params.append('vehicleModel', vehicleParams.vehicleModel);
      if (vehicleParams.vehicleYear) params.append('vehicleYear', vehicleParams.vehicleYear);
      if (vehicleParams.vehicleVariant) params.append('vehicleVariant', vehicleParams.vehicleVariant);
      const qs = params.toString();
      const response = await api.get(qs ? `/categories?${qs}` : '/categories');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch categories');
    }
  },

  /**
   * Get subcategories for a category
   * @param {string} category
   * @param {Object} vehicleParams - Optional vehicle filter params
   * @returns {Promise}
   */
  getSubCategories: async (category, vehicleParams = {}) => {
    try {
      const params = new URLSearchParams();
      if (vehicleParams.vehicleMake) params.append('vehicleMake', vehicleParams.vehicleMake);
      if (vehicleParams.vehicleModel) params.append('vehicleModel', vehicleParams.vehicleModel);
      if (vehicleParams.vehicleYear) params.append('vehicleYear', vehicleParams.vehicleYear);
      if (vehicleParams.vehicleVariant) params.append('vehicleVariant', vehicleParams.vehicleVariant);
      const qs = params.toString();
      const url = qs
        ? `/categories/${encodeURIComponent(category)}/subcategories?${qs}`
        : `/categories/${encodeURIComponent(category)}/subcategories`;
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch subcategories');
    }
  },

  /**
   * Get category with subcategories and sample products
   * @param {string} category
   * @param {Object} vehicleParams - Optional vehicle filter params
   * @returns {Promise}
   */
  getCategoryWithSubCategories: async (category, vehicleParams = {}) => {
    try {
      const params = new URLSearchParams();
      if (vehicleParams.vehicleMake) params.append('vehicleMake', vehicleParams.vehicleMake);
      if (vehicleParams.vehicleModel) params.append('vehicleModel', vehicleParams.vehicleModel);
      if (vehicleParams.vehicleYear) params.append('vehicleYear', vehicleParams.vehicleYear);
      if (vehicleParams.vehicleVariant) params.append('vehicleVariant', vehicleParams.vehicleVariant);
      const qs = params.toString();
      const url = qs
        ? `/categories/${encodeURIComponent(category)}/with-subcategories?${qs}`
        : `/categories/${encodeURIComponent(category)}/with-subcategories`;
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch category with subcategories');
    }
  },

  /**
   * VENDOR: Create new product
   * @param {Object} productData - Product data
   * @returns {Promise}
   */
  createVendorProduct: async (productData) => {
    try {
      const response = await api.post('/vendor/products', productData);
      return response.data.data || response.data;
    } catch (error) {
      // Handle validation errors
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const errorMessages = error.response.data.errors.join(', ');
        throw new Error(errorMessages || error.response?.data?.message || 'Validation failed');
      }
      throw new Error(error.response?.data?.message || 'Failed to create product');
    }
  },

  /**
   * PUBLIC: Field definitions for dynamic specs (by category)
   */
  getProductSpecTemplate: async ({ category = '', subCategory = '' } = {}) => {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (subCategory) params.append('subCategory', subCategory);
      const qs = params.toString();
      const response = await api.get(qs ? `/products/spec-template?${qs}` : '/products/spec-template');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load spec template');
    }
  },

  /**
   * VENDOR: Get own products
   * @param {Object} filters - Optional filters
   * @returns {Promise}
   */
  getVendorProducts: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.status) params.append('status', filters.status);
      if (filters.approved !== undefined) params.append('approved', filters.approved);
      if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
      if (filters.search) params.append('search', filters.search);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      
      const queryString = params.toString();
      const url = queryString ? `/vendor/products?${queryString}` : '/vendor/products';
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch products');
    }
  },

  /**
   * VENDOR: Update own product
   * @param {string} productId
   * @param {Object} productData
   * @returns {Promise}
   */
  updateVendorProduct: async (productId, productData) => {
    try {
      const response = await api.put(`/vendor/products/${productId}`, productData);
      return response.data.data || response.data;
    } catch (error) {
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        throw new Error(
          error.response.data.errors.join(', ') || error.response?.data?.message || 'Validation failed'
        );
      }
      throw new Error(error.response?.data?.message || 'Failed to update product');
    }
  },

  /**
   * VENDOR: Delete own product
   * @param {string} productId
   * @returns {Promise}
   */
  deleteVendorProduct: async (productId) => {
    try {
      const response = await api.delete(`/vendor/products/${productId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete product');
    }
  },
};

/**
 * Category Service - Handles all category-related API calls
 */
export const categoryService = {
  /**
   * ADMIN: Create new category
   * @param {Object} categoryData - Category data
   * @returns {Promise}
   */
  createCategory: async (categoryData) => {
    try {
      const response = await api.post('/admin/categories', categoryData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create category');
    }
  },

  /**
   * ADMIN: Get all categories
   * @param {Object} filters - Optional filters
   * @returns {Promise}
   */
  getAdminCategories: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.parentCategory !== undefined) params.append('parentCategory', filters.parentCategory);
      if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
      
      const queryString = params.toString();
      const url = queryString ? `/admin/categories?${queryString}` : '/admin/categories';
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch categories');
    }
  },

  /**
   * ADMIN: Get category by ID
   * @param {string} categoryId
   * @returns {Promise}
   */
  getCategoryById: async (categoryId) => {
    try {
      const response = await api.get(`/admin/categories/${categoryId}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch category');
    }
  },

  /**
   * ADMIN: Update category
   * @param {string} categoryId
   * @param {Object} categoryData
   * @returns {Promise}
   */
  updateCategory: async (categoryId, categoryData) => {
    try {
      const response = await api.put(`/admin/categories/${categoryId}`, categoryData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update category');
    }
  },

  /**
   * ADMIN: Delete category
   * @param {string} categoryId
   * @returns {Promise}
   */
  deleteCategory: async (categoryId) => {
    try {
      const response = await api.delete(`/admin/categories/${categoryId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete category');
    }
  },

  /**
   * PUBLIC: Get all active categories (for frontend)
   * @returns {Promise}
   */
  getActiveCategories: async () => {
    try {
      const response = await api.get('/categories');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch categories');
    }
  },

  /**
   * PUBLIC: Get all active categories as flat list (for vendor forms, filters)
   * @param {Object} vehicleParams - Optional { vehicleMake, vehicleModel, vehicleYear, vehicleVariant }
   * @returns {Promise}
   */
  getAllActiveCategoriesFlat: async (vehicleParams = {}) => {
    try {
      const params = new URLSearchParams();
      if (vehicleParams.vehicleMake) params.append('vehicleMake', vehicleParams.vehicleMake);
      if (vehicleParams.vehicleModel) params.append('vehicleModel', vehicleParams.vehicleModel);
      if (vehicleParams.vehicleYear) params.append('vehicleYear', vehicleParams.vehicleYear);
      if (vehicleParams.vehicleVariant) params.append('vehicleVariant', vehicleParams.vehicleVariant);
      if (vehicleParams.vehicleFuelType) params.append('vehicleFuelType', vehicleParams.vehicleFuelType);
      if (vehicleParams.condition) params.append('condition', vehicleParams.condition);
      if (vehicleParams.forVendor) params.append('forVendor', 'true');
      if (vehicleParams.catalogModelId) params.append('catalogModelId', vehicleParams.catalogModelId);
      const qs = params.toString();
      const response = await api.get(qs ? `/categories/flat?${qs}` : '/categories/flat');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch all active categories (flat)');
    }
  },
};

/**
 * Mechanic & Garage Service - Handles all mechanic and garage-related API calls
 */
export const mechanicGarageService = {
  /**
   * ADMIN: Create a new mechanic
   * @param {Object} mechanicData - { name, email, specialization, location, phone, status }
   * @returns {Promise}
   */
  createMechanic: async (mechanicData) => {
    try {
      const response = await api.post('/admin/mechanics', mechanicData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create mechanic');
    }
  },

  /**
   * ADMIN: Get all mechanics
   * @param {Object} filters - Optional filters
   * @returns {Promise}
   */
  getAllMechanics: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      
      const queryString = params.toString();
      const url = queryString ? `/admin/mechanics?${queryString}` : '/admin/mechanics';
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch mechanics');
    }
  },

  /**
   * ADMIN: Get mechanic by ID
   * @param {string} mechanicId
   * @returns {Promise}
   */
  getMechanicById: async (mechanicId) => {
    try {
      const response = await api.get(`/admin/mechanics/${mechanicId}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch mechanic');
    }
  },

  /**
   * ADMIN: Update mechanic
   * @param {string} mechanicId
   * @param {Object} mechanicData
   * @returns {Promise}
   */
  updateMechanic: async (mechanicId, mechanicData) => {
    try {
      const response = await api.put(`/admin/mechanics/${mechanicId}`, mechanicData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update mechanic');
    }
  },

  /**
   * ADMIN: Delete mechanic
   * @param {string} mechanicId
   * @returns {Promise}
   */
  deleteMechanic: async (mechanicId) => {
    try {
      const response = await api.delete(`/admin/mechanics/${mechanicId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete mechanic');
    }
  },

  /**
   * ADMIN: Create a new garage
   * @param {Object} garageData - { name, email, location, phone, status }
   * @returns {Promise}
   */
  createGarage: async (garageData) => {
    try {
      const response = await api.post('/admin/garages', garageData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create garage');
    }
  },

  /**
   * ADMIN: Cities with at least one active registered garage
   * @returns {Promise<string[]>}
   */
  getRegisteredGarageCities: async () => {
    try {
      const response = await api.get('/admin/garages/cities');
      const data = response.data?.data || response.data;
      return data?.cities ?? [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch garage cities');
    }
  },

  /**
   * ADMIN: Get all garages
   * @param {Object} filters - Optional filters
   * @returns {Promise}
   */
  getAllGarages: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      
      const queryString = params.toString();
      const url = queryString ? `/admin/garages?${queryString}` : '/admin/garages';
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch garages');
    }
  },

  /**
   * ADMIN: Get garage by ID
   * @param {string} garageId
   * @returns {Promise}
   */
  getGarageById: async (garageId) => {
    try {
      const response = await api.get(`/admin/garages/${garageId}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch garage');
    }
  },

  /**
   * ADMIN: Update garage
   * @param {string} garageId
   * @param {Object} garageData
   * @returns {Promise}
   */
  updateGarage: async (garageId, garageData) => {
    try {
      const response = await api.put(`/admin/garages/${garageId}`, garageData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update garage');
    }
  },

  /**
   * ADMIN: Delete garage
   * @param {string} garageId
   * @returns {Promise}
   */
  deleteGarage: async (garageId) => {
    try {
      const response = await api.delete(`/admin/garages/${garageId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete garage');
    }
  },

  /**
   * ADMIN: Reset password for mechanic/garage
   * @param {string} id
   * @param {string} type - 'mechanics' or 'garages'
   * @returns {Promise}
   */
  resetPassword: async (id, type) => {
    try {
      const response = await api.post(`/admin/${type}/${id}/reset-password`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reset password');
    }
  },
};

/**
 * Public garage listing (no auth) - Workshop Near You
 */
export const publicGarageService = {
  list: async () => {
    try {
      const response = await api.get('/garages');
      const data = response.data?.data || response.data;
      return data?.garages ?? data ?? [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch garages');
    }
  },

  /** CUST-01 — pincode/area search, service filter, pagination, radius expand */
  discover: async (params = {}) => {
    try {
      const qs = new URLSearchParams();
      qs.set('page', String(params.page || 1));
      if (params.pincode) qs.set('pincode', params.pincode);
      if (params.area) qs.set('area', params.area);
      if (params.serviceType) qs.set('serviceType', params.serviceType);
      if (params.clusterId != null && params.clusterId !== '') {
        qs.set('clusterId', String(params.clusterId));
      }
      if (params.fuelType) qs.set('fuelType', params.fuelType);
      if (params.discoveryMode) qs.set('discoveryMode', params.discoveryMode);
      if (params.sortBy) qs.set('sortBy', params.sortBy);
      if (params.lat != null && params.lat !== '') qs.set('lat', String(params.lat));
      if (params.lng != null && params.lng !== '') qs.set('lng', String(params.lng));
      if (params.limit) qs.set('limit', String(params.limit));
      const response = await api.get(`/garages?${qs.toString()}`);
      const data = response.data?.data || response.data;
      return {
        garages: data?.garages || [],
        pagination: data?.pagination || { page: 1, limit: 10, total: 0, pages: 1 },
        expandedRadius: Boolean(data?.expandedRadius),
        radiusKm: data?.radiusKm ?? null,
      };
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to discover garages');
    }
  },

  /** CUST-02 — garage profile + paginated reviews */
  getProfile: async (garageId, { reviewPage = 1 } = {}) => {
    try {
      const qs = reviewPage > 1 ? `?reviewPage=${encodeURIComponent(String(reviewPage))}` : '';
      const response = await api.get(`/garages/${garageId}${qs}`);
      const data = response.data?.data || response.data;
      return {
        garage: data?.garage || null,
        reviews: data?.reviews || { items: [], pagination: { page: 1, pages: 1, total: 0 } },
      };
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load garage profile');
    }
  },

  /** CUST-02 — available slots for a date (YYYY-MM-DD) */
  getSlots: async (garageId, date) => {
    try {
      const response = await api.get(`/garages/${garageId}/slots?date=${encodeURIComponent(date)}`);
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load garage slots');
    }
  },

  /** CUST-02 / CUST-03 — 7-day slot calendar summary */
  getSlotCalendar: async (garageId, days = 7) => {
    try {
      const response = await api.get(
        `/garages/${garageId}/slots?days=${encodeURIComponent(String(days))}`
      );
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load slot calendar');
    }
  },
};

/**
 * Garage service bookings (CUST-03)
 */
export const bookingService = {
  create: async (payload) => {
    try {
      const response = await api.post('/bookings/create', payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      const data = error.response?.data;
      const detail = Array.isArray(data?.errors) && data.errors.length
        ? data.errors.join(', ')
        : '';
      const err = new Error(
        detail ? `${data?.message || 'Failed to create booking'}: ${detail}` : (data?.message || 'Failed to create booking')
      );
      if (data?.code) err.code = data.code;
      err.status = error.response?.status;
      throw err;
    }
  },
  createSplit: async (payload) => {
    try {
      const response = await api.post('/bookings/create-split', payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      const data = error.response?.data;
      const err = new Error(data?.message || 'Failed to create split bookings');
      if (data?.code) err.code = data.code;
      err.status = error.response?.status;
      throw err;
    }
  },
  getStatus: async (bookingId) => {
    try {
      const response = await api.get(`/bookings/${bookingId}`);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load booking status');
    }
  },
  getStatusByJobCard: async (jobCardId) => {
    try {
      const response = await api.get(`/bookings/by-job/${jobCardId}`);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load booking status');
    }
  },
  listMy: async () => {
    try {
      const response = await api.get('/bookings/my');
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load your bookings');
    }
  },
};

/**
 * Job Cards (Service Requests) - Garage workflow lifecycle
 */
export const jobCardService = {
  create: async (payload) => {
    try {
      const response = await api.post('/jobcards', payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create job card');
    }
  },
  getMy: async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters).toString();
      const url = params ? `/jobcards/my?${params}` : '/jobcards/my';
      const response = await api.get(url);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch my job cards');
    }
  },
  getGarage: async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters).toString();
      const url = params ? `/jobcards/garage?${params}` : '/jobcards/garage';
      const response = await api.get(url);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch garage job cards');
    }
  },
  accept: async (id) => {
    try {
      const response = await api.post(`/jobcards/${id}/accept`);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to accept job');
    }
  },
  rejectGarage: async (id, reason = '') => {
    try {
      const response = await api.post(`/jobcards/${id}/garage-reject`, { reason });
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reject job');
    }
  },
  delete: async (id) => {
    try {
      const response = await api.delete(`/jobcards/${id}`);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete job');
    }
  },
  updateGarageIntake: async (id, garageIntake) => {
    try {
      const response = await api.put(`/jobcards/${id}/garage-intake`, { garageIntake });
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to save intake details');
    }
  },
  confirmIntake: async (id) => {
    try {
      const response = await api.post(`/jobcards/${id}/confirm-intake`);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to confirm intake');
    }
  },
  getAdmin: async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters).toString();
      const url = params ? `/admin/jobcards?${params}` : '/admin/jobcards';
      const response = await api.get(url);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch job cards');
    }
  },
  getById: async (id) => {
    try {
      const response = await api.get(`/jobcards/${id}`);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch job card');
    }
  },
  submitEstimate: async (id, payload) => {
    try {
      const response = await api.put(`/jobcards/${id}/estimate`, payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to submit estimate');
    }
  },
  adminUpdateEstimate: async (id, payload) => {
    try {
      const response = await api.put(`/admin/jobcards/${id}/estimate`, payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update estimate');
    }
  },
  approve: async (id, note = '') => {
    try {
      const response = await api.post(`/jobcards/${id}/approve`, { note });
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to approve estimate');
    }
  },
  reject: async (id, note = '') => {
    try {
      const response = await api.post(`/jobcards/${id}/reject`, { note });
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reject estimate');
    }
  },
  updateStatus: async (id, status, opts = {}) => {
    try {
      const response = await api.put(`/jobcards/${id}/status`, { status, ...opts });
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update status');
    }
  },
  deliver: async (id, payload) => {
    try {
      const response = await api.put(`/jobcards/${id}/deliver`, payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to mark delivered');
    }
  },
  paymentWebhook: async (payload) => {
    try {
      const response = await api.post('/jobcards/payment/webhook', payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to process payment webhook');
    }
  },
  patchItems: async (id, payload) => {
    try {
      const response = await api.patch(`/jobcards/${id}/items`, payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update job card items');
    }
  },
  getByShareToken: async (id, token) => {
    try {
      const response = await api.get(`/jobcards/${id}/review`, { params: { token } });
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch job card');
    }
  },
  getInvoice: async (id) => {
    try {
      const response = await api.get(`/jobcards/${id}/invoice`);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch invoice');
    }
  },
  downloadInvoice: async (id) => {
    try {
      const response = await api.get(`/jobcards/${id}/invoice/download`, { responseType: 'blob' });
      const blob = response.data;
      const filename = response.headers['content-disposition']
        ? response.headers['content-disposition'].split('filename=')[1]?.replace(/"/g, '').trim()
        : `invoice-${id}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to download invoice');
    }
  },
  createPayment: async (id, gateway = 'razorpay') => {
    try {
      const response = await api.post(`/jobcards/${id}/create-payment`, { gateway });
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create payment order');
    }
  },
  verifyPayment: async (id, payload) => {
    try {
      const response = await api.post(`/jobcards/${id}/payment/verify`, payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to verify payment');
    }
  },
  generateBridgeLink: async (id, payload = {}) => {
    try {
      const response = await api.post(`/jobcards/${id}/bridge-link`, payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to generate bridge link');
    }
  },
  generateAmendmentLink: async (id, payload = {}) => {
    try {
      const response = await api.post(`/jobcards/${id}/amendment-link`, payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to generate amendment link');
    }
  },
  generateCollectionLink: async (id) => {
    try {
      const response = await api.post(`/jobcards/${id}/collection-link`);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to generate collection link');
    }
  },
};

export const purchaseOrderService = {
  listByJobCard: async (jobCardId) => {
    try {
      const response = await api.get(`/purchase-orders/jobcard/${jobCardId}`);
      return response.data?.data ?? response.data ?? [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch purchase orders');
    }
  },
  listForGarage: async () => {
    try {
      const response = await api.get('/purchase-orders/garage');
      return response.data?.data ?? response.data ?? [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch purchase orders');
    }
  }
};

/**
 * ADMIN: Vehicle Management – list vehicles and fetch full history for a vehicle
 */
export const adminVehicleService = {
  /**
   * List vehicles across the platform with optional filters.
   * @param {Object} filters - { userId?, registrationNumber?, make?, model?, year?, vin? }
   */
  list: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.registrationNumber) params.append('registrationNumber', filters.registrationNumber);
    if (filters.make) params.append('make', filters.make);
    if (filters.model) params.append('model', filters.model);
    if (filters.year) params.append('year', filters.year);
    if (filters.vin) params.append('vin', filters.vin);
    const qs = params.toString();
    const url = qs ? `/admin/vehicles?${qs}` : '/admin/vehicles';
    const response = await api.get(url);
    return response.data?.data ?? response.data ?? [];
  },

  /**
   * Get a single vehicle with owner, orders and job cards.
   * @param {string} vehicleId
   */
  getDetails: async (vehicleId) => {
    const response = await api.get(`/admin/vehicles/${vehicleId}`);
    return response.data?.data ?? response.data;
  }
};

/**
 * ADMIN + public: hierarchical vehicle catalogue (maker → model → year / month / variant)
 */
export const vehicleCatalogService = {
  listMakersPublic: async ({ includeInactive = false } = {}) => {
    const qs = includeInactive ? '?includeInactive=true' : '';
    const response = await api.get(`/vehicle-catalog/makers${qs}`);
    return response.data?.data ?? response.data ?? [];
  },
  listModelsPublic: async (makerId, { includeInactive = false } = {}) => {
    const qs = includeInactive ? '?includeInactive=true' : '';
    const response = await api.get(`/vehicle-catalog/makers/${makerId}/models${qs}`);
    return response.data?.data ?? response.data ?? [];
  },
  listTrimsPublic: async (modelId, { includeInactive = false } = {}) => {
    const qs = includeInactive ? '?includeInactive=true' : '';
    const response = await api.get(`/vehicle-catalog/models/${modelId}/trims${qs}`);
    return response.data?.data ?? response.data ?? [];
  },
  listCategoriesPublic: async (modelId) => {
    const response = await api.get(`/vehicle-catalog/models/${modelId}/categories`);
    return response.data?.data ?? response.data ?? [];
  },
  listPartsPublic: async (categoryId, { modelId } = {}) => {
    const params = new URLSearchParams();
    if (modelId) params.append('modelId', String(modelId));
    const qs = params.toString();
    const response = await api.get(
      qs
        ? `/vehicle-catalog/model-categories/${categoryId}/parts?${qs}`
        : `/vehicle-catalog/model-categories/${categoryId}/parts`
    );
    return response.data?.data ?? response.data ?? [];
  }
};

export const adminVehicleCatalogService = {
  listMakers: async () => {
    const response = await api.get('/admin/vehicle-catalog/makers');
    return response.data?.data ?? response.data ?? [];
  },
  createMaker: async (body) => {
    const response = await api.post('/admin/vehicle-catalog/makers', body);
    return response.data?.data ?? response.data;
  },
  updateMaker: async (id, body) => {
    const response = await api.put(`/admin/vehicle-catalog/makers/${id}`, body);
    return response.data?.data ?? response.data;
  },
  deleteMaker: async (id) => {
    const response = await api.delete(`/admin/vehicle-catalog/makers/${id}`);
    return response.data?.data ?? response.data;
  },
  listModels: async (makerId) => {
    const response = await api.get(`/admin/vehicle-catalog/makers/${makerId}/models`);
    return response.data?.data ?? response.data ?? [];
  },
  createModel: async (makerId, body) => {
    const response = await api.post(`/admin/vehicle-catalog/makers/${makerId}/models`, body);
    return response.data?.data ?? response.data;
  },
  updateModel: async (id, body) => {
    const response = await api.put(`/admin/vehicle-catalog/models/${id}`, body);
    return response.data?.data ?? response.data;
  },
  deleteModel: async (id) => {
    const response = await api.delete(`/admin/vehicle-catalog/models/${id}`);
    return response.data?.data ?? response.data;
  },
  listTrims: async (modelId) => {
    const response = await api.get(`/admin/vehicle-catalog/models/${modelId}/trims`);
    return response.data?.data ?? response.data ?? [];
  },
  listModelProducts: async (modelId, { limit = 200 } = {}) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', String(limit));
    const qs = params.toString();
    const response = await api.get(
      qs
        ? `/admin/vehicle-catalog/models/${modelId}/products?${qs}`
        : `/admin/vehicle-catalog/models/${modelId}/products`
    );
    return response.data?.data ?? response.data ?? [];
  },
  createTrim: async (modelId, body) => {
    const response = await api.post(`/admin/vehicle-catalog/models/${modelId}/trims`, body);
    return response.data?.data ?? response.data;
  },
  updateTrim: async (id, body) => {
    const response = await api.put(`/admin/vehicle-catalog/trims/${id}`, body);
    return response.data?.data ?? response.data;
  },
  deleteTrim: async (id) => {
    const response = await api.delete(`/admin/vehicle-catalog/trims/${id}`);
    return response.data?.data ?? response.data;
  },
  listCategories: async (modelId) => {
    const response = await api.get(`/admin/vehicle-catalog/models/${modelId}/categories`);
    return response.data?.data ?? response.data ?? [];
  },
  createCategory: async (modelId, body) => {
    const response = await api.post(`/admin/vehicle-catalog/models/${modelId}/categories`, body);
    return response.data?.data ?? response.data;
  },
  updateCategory: async (id, body) => {
    const response = await api.put(`/admin/vehicle-catalog/model-categories/${id}`, body);
    return response.data?.data ?? response.data;
  },
  deleteCategory: async (id) => {
    const response = await api.delete(`/admin/vehicle-catalog/model-categories/${id}`);
    return response.data?.data ?? response.data;
  },
  listParts: async (categoryId, { modelId } = {}) => {
    const params = new URLSearchParams();
    if (modelId) params.append('modelId', String(modelId));
    const qs = params.toString();
    const response = await api.get(
      qs
        ? `/admin/vehicle-catalog/model-categories/${categoryId}/parts?${qs}`
        : `/admin/vehicle-catalog/model-categories/${categoryId}/parts`
    );
    return response.data?.data ?? response.data ?? [];
  },
  createPart: async (categoryId, body) => {
    const response = await api.post(
      `/admin/vehicle-catalog/model-categories/${categoryId}/parts`,
      body
    );
    return response.data?.data ?? response.data;
  },
  updatePart: async (id, body) => {
    const response = await api.put(`/admin/vehicle-catalog/model-spare-parts/${id}`, body);
    return response.data?.data ?? response.data;
  },
  deletePart: async (id) => {
    const response = await api.delete(`/admin/vehicle-catalog/model-spare-parts/${id}`);
    return response.data?.data ?? response.data;
  }
};

/**
 * Analytics Service - Handles all analytics-related API calls
 */
export const analyticsService = {
  /**
   * ADMIN: Get real-time dashboard data
   * @param {Object} filters - dateRange, location, etc.
   * @returns {Promise}
   */
  getRealTimeDashboard: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) params.append('dateRange', filters.dateRange);
      if (filters.location) params.append('location', filters.location);
      if (filters.city) params.append('city', filters.city);
      if (filters.state) params.append('state', filters.state);
      if (filters.country) params.append('country', filters.country);
      if (filters.area) params.append('area', filters.area);
      
      const queryString = params.toString();
      const url = queryString ? `/admin/analytics/dashboard?${queryString}` : '/admin/analytics/dashboard';
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch dashboard data');
    }
  },

  /**
   * ADMIN: Get revenue and commission data
   * @param {Object} filters - dateRange, groupBy
   * @returns {Promise}
   */
  getRevenueData: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) params.append('dateRange', filters.dateRange);
      if (filters.groupBy) params.append('groupBy', filters.groupBy);
      
      const queryString = params.toString();
      const url = queryString ? `/admin/analytics/revenue?${queryString}` : '/admin/analytics/revenue';
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch revenue data');
    }
  },

  /**
   * ADMIN: Get predictive analytics data
   * @returns {Promise}
   */
  getPredictiveAnalytics: async () => {
    try {
      const response = await api.get('/admin/analytics/predictive');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch predictive analytics');
    }
  },

  /**
   * ADMIN: Get quality control data
   * @returns {Promise}
   */
  getQualityControlData: async () => {
    try {
      const response = await api.get('/admin/analytics/quality-control');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch quality control data');
    }
  },

  /**
   * ADMIN: Get overall statistics
   * @param {Object} filters - dateRange
   * @returns {Promise}
   */
  getOverallStats: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) params.append('dateRange', filters.dateRange);
      if (filters.location) params.append('location', filters.location);
      if (filters.city) params.append('city', filters.city);
      if (filters.state) params.append('state', filters.state);
      if (filters.country) params.append('country', filters.country);
      if (filters.area) params.append('area', filters.area);
      
      const queryString = params.toString();
      const url = queryString ? `/admin/analytics/stats?${queryString}` : '/admin/analytics/stats';
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch overall statistics');
    }
  },

  /**
   * ADMIN: Get recent activities
   * @param {Object} filters - limit
   * @returns {Promise}
   */
  getRecentActivities: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.location) params.append('location', filters.location);
      if (filters.city) params.append('city', filters.city);
      if (filters.state) params.append('state', filters.state);
      if (filters.country) params.append('country', filters.country);
      if (filters.area) params.append('area', filters.area);
      
      const queryString = params.toString();
      const url = queryString ? `/admin/analytics/recent-activities?${queryString}` : '/admin/analytics/recent-activities';
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch recent activities');
    }
  },

  /**
   * ADMIN: Get best selling products
   * @param {Object} filters - dateRange, limit
   * @returns {Promise}
   */
  getBestSellingProducts: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) params.append('dateRange', filters.dateRange);
      if (filters.limit) params.append('limit', filters.limit);
      
      const queryString = params.toString();
      const url = queryString ? `/admin/analytics/best-selling?${queryString}` : '/admin/analytics/best-selling';
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch best selling products');
    }
  },

  /**
   * ADMIN: Get customer engagement metrics
   * @param {Object} filters - dateRange
   * @returns {Promise}
   */
  getCustomerEngagementMetrics: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) params.append('dateRange', filters.dateRange);
      
      const queryString = params.toString();
      const url = queryString ? `/admin/analytics/engagement?${queryString}` : '/admin/analytics/engagement';
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch customer engagement metrics');
    }
  },

  /**
   * ADMIN: Get system health
   * @returns {Promise}
   */
  getSystemHealth: async () => {
    try {
      const response = await api.get('/admin/analytics/system-health');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch system health');
    }
  },

  /**
   * ADMIN: Get active sessions
   * @returns {Promise}
   */
  getActiveSessions: async () => {
    try {
      const response = await api.get('/admin/analytics/active-sessions');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch active sessions');
    }
  },

  /**
   * ADMIN: Get top performing vendors
   * @param {Object} filters - dateRange, limit
   * @returns {Promise}
   */
  getTopVendors: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) params.append('dateRange', filters.dateRange);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.location) params.append('location', filters.location);
      if (filters.city) params.append('city', filters.city);
      if (filters.state) params.append('state', filters.state);
      if (filters.country) params.append('country', filters.country);
      if (filters.area) params.append('area', filters.area);
      
      const queryString = params.toString();
      const url = queryString ? `/admin/analytics/top-vendors?${queryString}` : '/admin/analytics/top-vendors';
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch top vendors');
    }
  },

  /**
   * ADMIN: Get list of known locations
   * @returns {Promise<Array<{value,label}>>}
   */
  getLocations: async () => {
    try {
      const response = await api.get('/admin/analytics/locations');
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch locations');
    }
  },

  /**
   * ADMIN: Export analytics data
   * @param {Object} filters - dateRange, type
   * @returns {Promise}
   */
  exportData: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) params.append('dateRange', filters.dateRange);
      if (filters.type) params.append('type', filters.type);
      
      const queryString = params.toString();
      const url = queryString ? `/admin/analytics/export?${queryString}` : '/admin/analytics/export';
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export data');
    }
  },

  /**
   * VENDOR: Get vendor dashboard data
   * @param {Object} filters - dateRange
   * @returns {Promise}
   */
  getVendorDashboard: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.dateRange) params.append('dateRange', filters.dateRange);
      
      const queryString = params.toString();
      const url = queryString ? `/vendor/analytics/dashboard?${queryString}` : '/vendor/analytics/dashboard';
      const response = await api.get(url);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch vendor dashboard data');
    }
  },
};

/**
 * Registration link (auth) - link a registration to the logged-in user (mobile must match)
 */
export const linkRegistrationToUser = async (registrationId) => {
  const response = await api.put(`/registration/${registrationId}/link`);
  return response.data?.data?.registration ?? response.data?.registration ?? response.data;
};

/**
 * Admin Registrations / KYC - list, approve, reject
 */
export const adminRegistrationsService = {
  list: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.kycStatus) params.append('kycStatus', filters.kycStatus);
      if (filters.role) params.append('role', filters.role);
      const queryString = params.toString();
      const url = queryString ? `/admin/registrations?${queryString}` : '/admin/registrations';
      const response = await api.get(url);
      const data = response.data?.data || response.data;
      return data?.registrations ?? data ?? [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch registrations');
    }
  },
  approve: async (registrationId) => {
    try {
      const response = await api.put(`/admin/registrations/${registrationId}/approve`);
      return response.data?.data?.registration ?? response.data?.registration ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to approve registration');
    }
  },
  reject: async (registrationId, rejectionReason, rejectedFields = []) => {
    try {
      const response = await api.put(`/admin/registrations/${registrationId}/reject`, {
        rejectionReason: rejectionReason || '',
        rejectedFields: Array.isArray(rejectedFields) ? rejectedFields : []
      });
      return response.data?.data?.registration ?? response.data?.registration ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reject registration');
    }
  },
};

/**
 * KYC model API – all KYC process through KYC model
 */
export const kycService = {
  list: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.role) params.append('role', filters.role);
    const q = params.toString();
    const url = q ? `/kyc/list?${q}` : '/kyc/list';
    const response = await api.get(url);
    const data = response.data?.data || response.data;
    return data?.kycs ?? data ?? [];
  },
  getById: async (kycId) => {
    const response = await api.get(`/kyc/${kycId}`);
    const data = response.data?.data || response.data;
    return data?.kyc ?? data;
  },
  getByUserId: async (userId) => {
    const response = await api.get(`/kyc/user/${userId}`);
    const data = response.data?.data || response.data;
    return data?.kyc ?? data?.kyc ?? null;
  },
  approve: async (kycId) => {
    const response = await api.put(`/kyc/${kycId}/approve`);
    const data = response.data?.data || response.data;
    return data?.kyc ?? data;
  },
  reject: async (kycId, rejectionReason, rejectedFields = []) => {
    const response = await api.put(`/kyc/${kycId}/reject`, {
      rejectionReason: rejectionReason || '',
      rejectedFields: Array.isArray(rejectedFields) ? rejectedFields : []
    });
    const data = response.data?.data || response.data;
    return data?.kyc ?? data;
  },
  getMyKyc: async () => {
    const response = await api.get('/kyc/my');
    return response.data?.data || response.data;
  },
  createOrGet: async (role) => {
    const response = await api.post('/kyc/create', { role });
    return response.data?.data?.kyc ?? response.data?.kyc ?? response.data;
  },
  addDocuments: async (kycId, documents) => {
    const response = await api.post(`/kyc/${kycId}/documents`, { documents });
    return response.data?.data?.kyc ?? response.data?.kyc ?? response.data;
  },
  submit: async (kycId) => {
    const response = await api.post(`/kyc/${kycId}/submit`);
    return response.data?.data?.kyc ?? response.data?.kyc ?? response.data;
  }
};

/**
 * Origin serving static files (uploads live at /uploads, not under /api).
 * Rewrites absolute URLs that still point at another host (e.g. stored http://localhost:5000/uploads/...)
 * so images work when REACT_APP_API_URL points at staging/production.
 */
export function getDocumentUrl(pathOrUrl) {
  if (!pathOrUrl || typeof pathOrUrl !== 'string') return '';
  const s = pathOrUrl.trim();
  const base = api.defaults.baseURL || process.env.REACT_APP_API_URL || '';
  const docBase = (base.replace(/\/api\/?$/, '') || base).replace(/\/$/, '');

  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      const u = new URL(s);
      if (!u.pathname.startsWith('/uploads/')) return s;
      if (!docBase) return s;
      const apiOrigin = new URL(docBase.endsWith('/') ? docBase : `${docBase}/`);
      const isLocal =
        u.hostname === 'localhost' ||
        u.hostname === '127.0.0.1' ||
        u.hostname === '[::1]';
      if (isLocal || u.hostname === apiOrigin.hostname) {
        return `${docBase}${u.pathname}`;
      }
      return s;
    } catch {
      return s;
    }
  }

  const path = s.startsWith('/') ? s : `/${s}`;
  if (!docBase) return path;
  return `${docBase}${path}`;
}

/**
 * B-03 Search Results — catalog nodes + champion/listings
 */
/**
 * B-04 Part Detail — listing, Q&A, cross-sell, cart add by listingId
 */
export const usedListingService = {
  getListing: async (listingId, params = {}) => {
    const qs = new URLSearchParams({ type: 'used' });
    if (params.vehicleId) qs.set('vehicleId', params.vehicleId);
    try {
      const response = await api.get(`/listings/${listingId}?${qs.toString()}`);
      const data = response.data;
      return data?.listing || data?.data?.listing || data;
    } catch (error) {
      const err = new Error(error.response?.data?.message || 'Failed to load used listing');
      err.status = error.response?.status;
      throw err;
    }
  },

  checkAvailability: async ({ listingId, vehicleId }) => {
    try {
      const response = await api.post('/orders/check-availability', {
        listingId,
        vehicleId: vehicleId || undefined,
      });
      return response.data?.data || response.data;
    } catch (error) {
      const err = new Error(error.response?.data?.message || 'Check availability failed');
      err.status = error.response?.status;
      throw err;
    }
  },

  expressBuy: async ({ listingId, vehicleId }) => {
    try {
      const response = await api.post('/orders/express-buy', {
        listingId,
        vehicleId: vehicleId || undefined,
      });
      return response.data?.data || response.data;
    } catch (error) {
      const err = new Error(error.response?.data?.message || 'Express buy failed');
      err.status = error.response?.status;
      throw err;
    }
  },

  proceedAfterVerificationPhoto: async ({ orderId, listingId }) => {
    try {
      const response = await api.post('/orders/proceed-after-verification-photo', {
        orderId,
        listingId: listingId || undefined,
      });
      return response.data?.data || response.data;
    } catch (error) {
      const err = new Error(
        error.response?.data?.message || 'Could not proceed to checkout'
      );
      err.status = error.response?.status;
      err.code = error.response?.data?.code;
      throw err;
    }
  },
};

/** B-05 — Seller-side check availability (listing owner) */
export const sellerAvailabilityService = {
  listRequests: async () => {
    try {
      const response = await api.get('/seller/availability-requests');
      const data = response.data?.data || response.data;
      return data?.requests || [];
    } catch (error) {
      throw new Error(
        error.response?.data?.message || 'Failed to load availability requests'
      );
    }
  },

  submitVerificationPhoto: async (orderId, photoUrl) => {
    try {
      const response = await api.post(
        `/seller/availability-requests/${encodeURIComponent(orderId)}/verification-photo`,
        { photoUrl }
      );
      return response.data?.data?.request || response.data?.request;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || 'Failed to submit verification photo'
      );
    }
  },
};

export const bodyListingService = {
  getListing: async (listingId, params = {}) => {
    const qs = new URLSearchParams({ type: 'body' });
    if (params.vehicleId) qs.set('vehicleId', params.vehicleId);
    try {
      const response = await api.get(`/listings/${listingId}?${qs.toString()}`);
      const data = response.data;
      return data?.listing || data?.data?.listing || data;
    } catch (error) {
      const err = new Error(error.response?.data?.message || 'Failed to load body listing');
      err.status = error.response?.status;
      throw err;
    }
  },

  checkAvailability: async ({ listingId, vehicleId }) => {
    try {
      const response = await api.post('/orders/check-availability', {
        listingId,
        vehicleId: vehicleId || undefined,
      });
      return response.data?.data || response.data;
    } catch (error) {
      const err = new Error(error.response?.data?.message || 'Check availability failed');
      err.status = error.response?.status;
      throw err;
    }
  },

  approveDispatchPhoto: async ({ orderId, listingId }) => {
    try {
      const response = await api.post('/orders/approve-dispatch-photo', {
        orderId,
        listingId: listingId || undefined,
      });
      return response.data?.data || response.data;
    } catch (error) {
      const err = new Error(error.response?.data?.message || 'Dispatch photo approval failed');
      err.status = error.response?.status;
      throw err;
    }
  },
};

export const listingService = {
  getListing: async (listingId, params = {}) => {
    const qs = new URLSearchParams();
    if (params.vehicleId) qs.set('vehicleId', params.vehicleId);
    if (params.type) qs.set('type', params.type);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    try {
      const response = await api.get(`/listings/${listingId}${suffix}`);
      const data = response.data;
      return data?.listing || data?.data?.listing || data;
    } catch (error) {
      const err = new Error(
        error.response?.data?.message || 'Failed to load listing'
      );
      err.status = error.response?.status;
      err.code = error.response?.data?.code;
      throw err;
    }
  },

  getCrossSell: async (nodeId, params = {}) => {
    const qs = new URLSearchParams();
    if (params.excludeListingId) qs.set('excludeListingId', params.excludeListingId);
    if (params.limit) qs.set('limit', String(params.limit));
    const response = await api.get(`/catalog/${nodeId}/cross-sell?${qs.toString()}`);
    return response.data;
  },

  getQa: async (nodeId) => {
    const response = await api.get(`/catalog/${nodeId}/qa`);
    return response.data;
  },

  postQa: async (nodeId, question) => {
    const response = await api.post(`/catalog/${nodeId}/qa`, { question });
    return response.data;
  },

  addToCart: async ({ listingId, quantity = 1, vehicleId }) => {
    try {
      const response = await api.post('/cart/add', {
        listingId,
        quantity,
        vehicleId: vehicleId || undefined,
      });
      return response.data?.data || response.data;
    } catch (error) {
      const err = new Error(
        error.response?.data?.message || 'Failed to add to cart'
      );
      err.status = error.response?.status;
      err.code = error.response?.data?.code;
      throw err;
    }
  },
};

export const searchService = {
  search: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.category) qs.set('category', params.category);
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    if (params.vehicleId) qs.set('vehicleId', params.vehicleId);
    if (params.page) qs.set('page', String(params.page));
    if (params.filters && Object.keys(params.filters).length) {
      qs.set('filters', JSON.stringify(params.filters));
    }
    const response = await api.get(`/search?${qs.toString()}`);
    return response.data;
  },

  getChampion: async (nodeId, params = {}) => {
    const qs = new URLSearchParams();
    if (params.column) qs.set('column', params.column);
    if (params.q) qs.set('q', params.q);
    if (params.vehicleId) qs.set('vehicleId', params.vehicleId);
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    const response = await api.get(`/catalog/${nodeId}/champion?${qs.toString()}`);
    return response.data;
  },

  getListings: async (nodeId, params = {}) => {
    const qs = new URLSearchParams();
    if (params.column) qs.set('column', params.column);
    if (params.q) qs.set('q', params.q);
    if (params.vehicleId) qs.set('vehicleId', params.vehicleId);
    if (params.sortBy) qs.set('sortBy', params.sortBy);
    const response = await api.get(`/catalog/${nodeId}/listings?${qs.toString()}`);
    return response.data;
  },
};

/** 7-layer catalog node (SHA-256) — resolve, fetch, publish */
export const catalogNodeService = {
  searchParts: async (query, params = {}) => {
    const qs = new URLSearchParams();
    if (query) qs.set('q', query);
    if (params.make) qs.set('make', params.make);
    if (params.model) qs.set('model', params.model);
    if (params.limit) qs.set('limit', String(params.limit));
    const response = await api.get(`/catalog-nodes/parts/search?${qs.toString()}`);
    return response.data;
  },

  getFitmentFields: async (profile) => {
    const response = await api.get(`/catalog-nodes/fitment-fields?profile=${encodeURIComponent(profile || 'B')}`);
    return response.data;
  },

  getFitmentFieldsByPart: async (partCategory) => {
    const response = await api.get(
      `/catalog-nodes/fitment-fields?partCategory=${encodeURIComponent(partCategory)}`
    );
    return response.data;
  },

  previewCrossCompat: async (payload) => {
    const response = await api.post('/catalog-nodes/fitment/preview-cross-compat', payload);
    return response.data;
  },

  adminFitmentOverride: async (payload) => {
    const response = await api.post('/admin/fitment-overrides', payload);
    return response.data;
  },

  listVendorListings: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await api.get(`/vendor/catalog-listings${suffix}`);
    return response.data;
  },

  getVendorListing: async (listingId) => {
    const response = await api.get(`/vendor/catalog-listings/${listingId}`);
    return response.data;
  },

  resolve: async (payload) => {
    const response = await api.post('/catalog-nodes/resolve', payload);
    return response.data;
  },

  getById: async (catalogNodeId, params = {}) => {
    const qs = new URLSearchParams();
    if (params.condition) qs.set('condition', params.condition);
    if (params.status) qs.set('status', params.status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await api.get(`/catalog-nodes/${catalogNodeId}${suffix}`);
    return response.data;
  },

  listGaps: async () => {
    const response = await api.get('/admin/catalog-gaps');
    return response.data;
  },

  resolveGap: async (gapId) => {
    const response = await api.post(`/admin/catalog-gaps/${gapId}/resolve`);
    return response.data;
  },

  listApprovals: async () => {
    const response = await api.get('/admin/catalog-approvals');
    return response.data;
  },

  approveApproval: async (approvalId) => {
    const response = await api.post(`/admin/catalog-approvals/${approvalId}/approve`);
    return response.data;
  },

  rejectApproval: async (approvalId, reason = '') => {
    const response = await api.post(`/admin/catalog-approvals/${approvalId}/reject`, { reason });
    return response.data;
  },

  publishListing: async (payload) => {
    const response = await api.post('/catalog-nodes/listings', payload);
    return response.data;
  },

  validateOemPhoto: async (oemPhotoUrl, partName = '') => {
    const response = await api.post('/catalog-nodes/listings/oem-photo/validate', {
      oemPhotoUrl,
      partName,
    });
    return response.data;
  },

  updateListing: async (listingId, updates) => {
    const response = await api.patch(`/catalog-nodes/listings/${listingId}`, updates);
    return response.data;
  },

  listSellerReviewQueue: async () => {
    const response = await api.get('/admin/seller-review-queue');
    return response.data;
  },

  clearSellerReviewQueue: async (sellerId, note = '') => {
    const response = await api.post(`/admin/seller-review-queue/${sellerId}/clear`, { note });
    return response.data;
  },

  suspendSellerFromReview: async (sellerId, { days = 7, reason = '' } = {}) => {
    const response = await api.post(`/admin/seller-review-queue/${sellerId}/suspend`, { days, reason });
    return response.data;
  },
};

/** Product discovery — 4 entry points → catalogNodeId */
export const discoveryService = {
  keywordSearch: async (q, { vehicleId } = {}) => {
    const qs = new URLSearchParams();
    if (q) qs.set('q', q);
    if (vehicleId) qs.set('vehicleId', vehicleId);
    const response = await api.get(`/discovery/search?${qs.toString()}`);
    return response.data;
  },

  browseVehicle: async (vehicleId) => {
    const response = await api.get(`/discovery/vehicle/${vehicleId}`);
    return response.data;
  },

  lookupRegistration: async (reg, params = {}) => {
    const qs = new URLSearchParams({ reg });
    if (params.save) qs.set('save', '1');
    if (params.generationId) qs.set('generationId', params.generationId);
    if (params.variantId) qs.set('variantId', params.variantId);
    if (params.fuelType) qs.set('fuelType', params.fuelType);
    const response = await api.get(`/discovery/vahan?${qs.toString()}`);
    return response.data;
  },

  browseSubSystems: async (vehicleId) => {
    const qs = vehicleId ? `?vehicleId=${encodeURIComponent(vehicleId)}` : '';
    const response = await api.get(`/discovery/subsystems${qs}`);
    return response.data;
  },

  browseSubSystemCategories: async (subSystem, vehicleId) => {
    const qs = new URLSearchParams();
    if (vehicleId) qs.set('vehicleId', vehicleId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await api.get(
      `/discovery/subsystem/${encodeURIComponent(subSystem)}${suffix}`
    );
    return response.data;
  },

  getCatalogNodePage: async (catalogNodeId, { condition, pincode } = {}) => {
    const qs = new URLSearchParams();
    if (condition) qs.set('condition', condition);
    if (pincode) qs.set('pincode', pincode);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await api.get(`/discovery/catalog-nodes/${catalogNodeId}${suffix}`);
    return response.data;
  },

  getCatalogNodeSsrUrl: (catalogNodeId, { condition, pincode } = {}) => {
    const base = api.defaults.baseURL || '';
    const qs = new URLSearchParams();
    if (condition) qs.set('condition', condition);
    if (pincode) qs.set('pincode', pincode);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return `${base}/discovery/catalog-nodes/${catalogNodeId}/ssr${suffix}`;
  },

  requestCatalogNode: async (payload) => {
    const response = await api.post('/discovery/catalog-nodes/request', payload);
    return response.data;
  },

  resolveDestination: async (payload) => {
    const response = await api.post('/discovery/resolve', payload);
    return response.data;
  },

  unifiedSearch: async ({ mode = 'part', q, oem, vehicleId, vinLast6, vin, pincode, limit } = {}) => {
    const qs = new URLSearchParams();
    qs.set('mode', mode);
    if (q) qs.set('q', q);
    if (oem) qs.set('oem', oem);
    if (vehicleId) qs.set('vehicleId', vehicleId);
    if (vinLast6) qs.set('vinLast6', vinLast6);
    if (vin) qs.set('vin', vin);
    if (pincode) qs.set('pincode', pincode);
    if (limit) qs.set('limit', String(limit));
    const response = await api.get(`/discovery/search-engine?${qs.toString()}`);
    return response.data;
  },

  searchAutocomplete: async (q) => {
    const response = await api.get(`/discovery/search/autocomplete?q=${encodeURIComponent(q)}`);
    return response.data;
  },

  logPhotoSearchTap: async () => {
    const response = await api.post('/discovery/search/photo-tap');
    return response.data;
  },
};

/** Seller marketplace order lifecycle (2h acceptance SLA) */
export const sellerLifecycleService = {
  acceptOrder: async (orderId, payload = {}) => {
    const response = await api.post(`/seller/orders/${orderId}/accept`, payload);
    return response.data?.data || response.data;
  },
  markPacking: async (orderId) => {
    const response = await api.post(`/seller/orders/${orderId}/packing`);
    return response.data?.data || response.data;
  },
  markDispatched: async (orderId) => {
    const response = await api.post(`/seller/orders/${orderId}/dispatch`);
    return response.data?.data || response.data;
  },
  markInTransit: async (orderId) => {
    const response = await api.post(`/seller/orders/${orderId}/in-transit`);
    return response.data?.data || response.data;
  },
  markDelivered: async (orderId) => {
    const response = await api.post(`/seller/orders/${orderId}/delivered`);
    return response.data?.data || response.data;
  },
  cancelOrder: async (orderId, reason) => {
    const response = await api.post(`/seller/orders/${orderId}/cancel`, { reason });
    return response.data?.data || response.data;
  },
  reportOutOfStock: async (orderId, reason) => {
    const response = await api.post(`/seller/orders/${orderId}/out-of-stock`, { reason });
    return response.data?.data || response.data;
  },
};

/**
 * Home Feed (B-01) — launchpad APIs
 */
export const warrantyService = {
  listWarranties: async (status = 'all') => {
    try {
      const qs =
        status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
      const response = await api.get(`/warranties${qs}`);
      const data = response.data?.data || response.data;
      return data?.warranties || [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load warranties');
    }
  },

  getWarranty: async (warrantyId) => {
    try {
      const response = await api.get(`/warranties/${encodeURIComponent(warrantyId)}`);
      const data = response.data?.data || response.data;
      return data?.warranty || data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load warranty');
    }
  },

  raiseClaim: async (warrantyId, payload) => {
    try {
      const response = await api.post(
        `/warranties/${encodeURIComponent(warrantyId)}/claim`,
        payload
      );
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to submit warranty claim');
    }
  },
};

export const accountSettingsService = {
  getWishlist: async () => {
    try {
      const response = await api.get('/user/wishlist');
      const data = response.data?.data || response.data;
      return data?.items || [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load wishlist');
    }
  },

  addToWishlist: async (payload) => {
    try {
      const response = await api.post('/user/wishlist', payload);
      const data = response.data?.data || response.data;
      return data?.item || data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to save to wishlist');
    }
  },

  removeFromWishlist: async (id) => {
    try {
      await api.delete(`/user/wishlist/${encodeURIComponent(id)}`);
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to remove item');
    }
  },

  getNotificationPreferences: async () => {
    try {
      const response = await api.get('/user/notifications');
      const data = response.data?.data || response.data;
      return data?.preferences || data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load notification preferences');
    }
  },

  updateNotificationPreferences: async (patch) => {
    try {
      const response = await api.patch('/user/notifications', patch);
      const data = response.data?.data || response.data;
      return data?.preferences || data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update preferences');
    }
  },

  deleteAccount: async () => {
    try {
      const response = await api.post('/user/delete-account');
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete account');
    }
  },

  getWallet: async () => {
    try {
      const response = await api.get('/user/wallet');
      const data = response.data?.data || response.data;
      return data?.wallet || data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load wallet');
    }
  },

  getReferral: async () => {
    try {
      const response = await api.get('/user/referral');
      const data = response.data?.data || response.data;
      return data?.referral || data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load referral link');
    }
  },

  clearSearchHistory: async () => {
    try {
      const response = await api.delete('/user/recent-searches');
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to clear search history');
    }
  },

  requestDataExport: async () => {
    try {
      const response = await api.post('/user/data-export-request');
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to submit data export request');
    }
  },

  getAddresses: async () => {
    try {
      const response = await api.get('/user/addresses');
      const data = response.data?.data || response.data;
      return data?.addresses || [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load addresses');
    }
  },

  addAddress: async (payload) => {
    try {
      const response = await api.post('/user/addresses', payload);
      const data = response.data?.data || response.data;
      return data?.address || data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add address');
    }
  },

  updateAddress: async (id, payload) => {
    try {
      const response = await api.patch(`/user/addresses/${encodeURIComponent(id)}`, payload);
      const data = response.data?.data || response.data;
      return data?.address || data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update address');
    }
  },

  deleteAddress: async (id) => {
    try {
      await api.delete(`/user/addresses/${encodeURIComponent(id)}`);
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete address');
    }
  },

  setDefaultAddress: async (id) => {
    try {
      const response = await api.post(`/user/addresses/${encodeURIComponent(id)}/default`);
      const data = response.data?.data || response.data;
      return data?.address || data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to set default address');
    }
  },
};

export const passportService = {
  getPassport: async (vehicleId) => {
    try {
      const response = await api.get(`/passport/${encodeURIComponent(vehicleId)}`);
      const data = response.data?.data || response.data;
      return data?.passport || data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load health passport');
    }
  },

  createShareLink: async (vehicleId) => {
    try {
      const response = await api.post('/passport/share', { vehicleId });
      return response.data?.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create share link');
    }
  },

  getPublicPassport: async (shareToken) => {
    try {
      const response = await api.get(`/passport/public/${encodeURIComponent(shareToken)}`);
      const data = response.data?.data || response.data;
      return data?.passport || data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Share link invalid or expired');
    }
  },

  exportPdf: async (vehicleId) => {
    try {
      const response = await api.get(`/passport/${encodeURIComponent(vehicleId)}/export`, {
        responseType: 'blob',
      });
      const blob = response.data;
      const filename = response.headers['content-disposition']
        ? response.headers['content-disposition'].split('filename=')[1]?.replace(/"/g, '').trim()
        : `health-passport-${vehicleId}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export PDF');
    }
  },
};

export const homeFeedService = {
  getHomeFeed: async () => {
    const response = await api.get('/user/home-feed');
    return response.data;
  },

  getTrending: async () => {
    const response = await api.get('/catalog/trending');
    const data = response.data;
    return data?.tiles || data?.data?.tiles || [];
  },

  getRecentSearches: async () => {
    const response = await api.get('/user/recent-searches');
    const data = response.data;
    return data?.searches || data?.data?.searches || [];
  },

  recordRecentSearch: async (term) => {
    const response = await api.post('/user/recent-searches', { term });
    const data = response.data;
    return data?.searches || data?.data?.searches || [];
  },
};

/**
 * Phase 5: Custom workshop quote + transformer project intake.
 */
export const garageSpecialRequestService = {
  createQuoteRequest: async (payload) => {
    try {
      const response = await api.post('/garage-quote-requests', payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to submit quote request');
    }
  },

  createTransformationProject: async (payload) => {
    try {
      const response = await api.post('/transformation-projects', payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to submit project intake');
    }
  },

  listMyQuoteRequests: async () => {
    try {
      const response = await api.get('/garage-quote-requests/my');
      const data = response.data?.data || response.data;
      return Array.isArray(data?.requests) ? data.requests : [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load quote requests');
    }
  },

  updateMyQuoteStatus: async (id, payload) => {
    try {
      const response = await api.patch(`/garage-quote-requests/${id}/status`, payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update quote request');
    }
  },

  listMyTransformationProjects: async () => {
    try {
      const response = await api.get('/transformation-projects/my');
      const data = response.data?.data || response.data;
      return Array.isArray(data?.projects) ? data.projects : [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load projects');
    }
  },

  updateMyTransformationStatus: async (id, payload) => {
    try {
      const response = await api.patch(`/transformation-projects/${id}/status`, payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update project');
    }
  },
};

/** Use for any product/part image URL from the API (relative /uploads or legacy absolute localhost). */
export function resolveProductMediaUrl(u) {
  if (u == null || typeof u !== 'string') return '';
  const s = u.trim();
  if (!s) return '';
  if (s.startsWith('data:') || s.startsWith('blob:')) return s;
  return getDocumentUrl(s);
}